/**
 * What the chat's activity line says while the model thinks over what it just got back. Each case
 * holds interchangeable phrasings; one is picked per call, so consecutive turns read differently.
 * `{n}` is the count the case is about. The ellipsis is added where a phrase is shown.
 */

import type { PhraseSet } from '@/types/chat';

/** After a call that changed the screen: the model carries on from there. */
const CARRYING_ON: PhraseSet = {
    any: ['Carrying on', 'Moving on', 'Continuing', 'On to the next step'],
};

/** Keyed by the tool's bare name, without the agent's `vms__` / `metadata__` / `ui__`. */
export const TOOL_PHRASES: Record<string, PhraseSet> = {
    search_moments: {
        images: {
            one: [
                'Examining the best match',
                'Looking at the top moment',
                'Checking what the best match shows',
            ],
            many: [
                'Examining {n} matching frames',
                'Looking at the top {n} moments',
                'Comparing {n} candidate frames',
                'Checking what the {n} best matches show',
                'Studying {n} frames from the archive',
            ],
        },
        items: {
            one: ['Going over the only match', 'Reading the one moment found'],
            many: [
                'Going over {n} moments',
                'Sorting through {n} matches',
                'Weighing {n} candidate moments',
                'Reviewing {n} matching moments',
            ],
        },
        none: ['No moments matched', 'The video search came back empty', 'Nothing matched'],
        any: [
            'Reviewing the matching moments',
            'Going over the search results',
            'Sorting through the matches',
        ],
    },
    search_objects: {
        items: {
            one: ['Looking at the object found', 'Checking the one match', 'Reading the sighting'],
            many: [
                'Looking through {n} objects',
                'Sorting through {n} sightings',
                'Going over {n} detections',
                'Comparing {n} matching objects',
                'Reviewing {n} sightings',
            ],
        },
        none: ['No objects matched', 'No sightings found', 'The object search came back empty'],
        any: [
            'Looking through the objects found',
            'Reviewing the sightings',
            'Going over the detections',
        ],
    },
    search_activity: {
        items: {
            one: ['Reading the matching event', 'Reading the one entry found'],
            many: [
                'Reading through {n} events',
                'Going over {n} activity entries',
                'Piecing together {n} events',
                'Reviewing {n} events',
                'Looking into {n} events',
            ],
        },
        none: ['No activity found', 'No matching events', 'The activity search came back empty'],
        any: ['Looking into the events', 'Reading the activity log', 'Going over the activity'],
    },
    list_enrolled_objects: {
        items: {
            one: ['Checking the enrolled identity', 'Reading the one enrolled name'],
            many: [
                'Checking {n} enrolled identities',
                'Going over {n} known identities',
                'Reading {n} enrolled names',
            ],
        },
        none: ['No identities are enrolled', 'The enrollment list is empty'],
        any: ['Checking the enrolled identities', 'Reading the enrollment list'],
    },
    get_event: {
        any: [
            'Reading the event details',
            'Looking into the event',
            'Going over the event record',
            'Checking what the event says',
        ],
    },
    get_event_image: {
        images: {
            many: [
                'Examining {n} event images',
                'Comparing {n} snapshots',
                'Looking at {n} event frames',
            ],
        },
        any: [
            'Examining the event image',
            'Looking at the snapshot',
            'Studying the event frame',
            'Checking what the image shows',
        ],
    },

    get_camera_frame: {
        images: {
            many: [
                'Examining {n} camera frames',
                'Comparing {n} frames',
                'Checking what {n} cameras see',
                'Looking at {n} views side by side',
            ],
        },
        any: [
            'Examining the frame',
            'Looking closely at the frame',
            'Checking what the camera sees',
            'Studying the image',
            'Taking a close look at the scene',
        ],
    },
    await_change_in_video: {
        images: {
            one: ['Checking the last frame that still shows it', 'Looking at the farthest frame'],
            many: [
                'Checking {n} frames across the change',
                'Picking the moment from {n} frames',
                'Looking through {n} frames of the change',
            ],
        },
        any: ['Checking the frames of the change', 'Picking the moment of the change'],
    },
    find_change_in_video: {
        any: ['Gathering what the search needs', 'Looking for a hint while the search runs'],
    },
    get_camera_frames: {
        items: {
            one: ['Examining the frame', 'Looking at the one frame found'],
            many: [
                'Stepping through {n} frames',
                'Examining {n} frames in order',
                'Comparing {n} frames over time',
                'Going through {n} frames one by one',
                'Studying {n} frames from the archive',
            ],
        },
        none: ['No recorded frames in that window', 'No footage for that period'],
        any: ['Examining the frames', 'Stepping through the footage', 'Comparing the frames'],
    },
    list_cameras: {
        items: {
            one: ['Reading the camera details', 'Checking the camera'],
            many: [
                'Going over {n} cameras',
                'Checking {n} cameras',
                'Reviewing the {n} cameras',
                'Looking through {n} camera setups',
            ],
        },
        none: ['No cameras found', 'The organization has no cameras yet'],
        any: ['Going over the cameras', 'Reading the camera list'],
    },
    get_camera: {
        any: [
            'Reading the camera settings',
            'Checking the camera details',
            'Going over the camera setup',
            'Looking at how the camera is set up',
        ],
    },
    list_servers: {
        items: {
            one: ['Checking the server', 'Reading the server status'],
            many: [
                'Checking {n} servers',
                'Going over {n} servers',
                'Reviewing the status of {n} servers',
            ],
        },
        none: ['No servers found', 'The server list is empty'],
        any: ['Checking the servers', 'Reading the server list'],
    },
    list_rules: {
        items: {
            one: ['Reading the rule', 'Checking the one rule'],
            many: ['Reading {n} rules', 'Going over {n} rules', 'Checking {n} automation rules'],
        },
        none: ['No rules are set up yet', 'The rule list is empty'],
        any: ['Reading the rules', 'Going over the rules'],
    },
    get_rule: {
        any: [
            'Reading the rule',
            'Going over the rule details',
            'Checking how the rule is set up',
            'Reading the rule’s triggers and actions',
        ],
    },
    get_rules_schema: {
        any: [
            'Studying the rule options',
            'Checking what rules can do',
            'Reading the rule schema',
            'Going over the available triggers and actions',
        ],
    },
    list_agents: {
        items: {
            one: ['Checking the analytics agent', 'Reading the agent details'],
            many: [
                'Going over {n} analytics agents',
                'Checking {n} agents',
                'Reviewing {n} installed agents',
            ],
        },
        none: ['No analytics agents installed', 'No agents found'],
        any: ['Going over the analytics agents', 'Reading the agent list'],
    },
    list_analytics_categories: {
        items: {
            one: ['Checking the one category', 'Reading the analytics category'],
            many: [
                'Going over {n} analytics categories',
                'Checking {n} detection types',
                'Reading {n} categories',
            ],
        },
        none: ['No analytics categories found', 'No categories are available'],
        any: ['Reading the analytics categories', 'Checking what can be detected'],
    },
    get_device_agent_settings: {
        any: [
            'Reading the agent settings',
            'Checking how the analytics are tuned',
            'Going over the agent settings',
        ],
    },
    get_suggestions: {
        items: {
            one: ['Reviewing suggestions for the camera', 'Going over the camera’s tips'],
            many: [
                'Reviewing suggestions for {n} cameras',
                'Going over tips for {n} cameras',
                'Weighing improvements for {n} cameras',
            ],
        },
        none: ['No suggestions found', 'No improvements suggested'],
        any: ['Reviewing the suggestions', 'Going over the improvement tips'],
    },
    get_fleet_update_status: {
        any: [
            'Checking the update status',
            'Reading the fleet update report',
            'Going over server versions',
            'Checking which servers need updates',
        ],
    },

    show_camera: {
        any: [
            'Continuing with the camera open',
            'Camera is up, carrying on',
            'Carrying on with the camera in view',
        ],
    },
    show_cameras: {
        any: [
            'Continuing with the cameras open',
            'Cameras are up, carrying on',
            'Carrying on with the cameras in view',
        ],
    },
    seek_video: {
        any: [
            'Continuing from that moment',
            'Carrying on at the new position',
            'Picking up from that point',
        ],
    },
    get_view_state: {
        any: [
            'Checking what is on screen',
            'Reading the current view',
            'Looking at the open panels',
            'Taking in the screen',
        ],
    },
    export_clip: {
        any: ['Carrying on while the clip exports', 'Clip requested, carrying on'],
    },
    navigate: CARRYING_ON,
    open_panel: CARRYING_ON,
    open_objects_panel: CARRYING_ON,
    open_events_panel: CARRYING_ON,
    set_panel_prop: CARRYING_ON,
    close_panel: CARRYING_ON,
};

/** A tool with no set of its own. */
export const DEFAULT_PHRASES: PhraseSet = {
    images: {
        one: ['Looking at the image', 'Examining the image'],
        many: ['Looking at {n} images', 'Examining {n} images', 'Comparing {n} images'],
    },
    items: {
        one: ['Reading the result', 'Checking the result'],
        many: ['Going over {n} results', 'Sorting through {n} results', 'Reviewing {n} results'],
    },
    none: ['The call came back empty', 'Nothing came back'],
    any: [
        'Reviewing the results',
        'Going over what came back',
        'Reading the results',
        'Taking stock',
    ],
};

/** Every call of the last round failed, or an approved change did. */
export const FAILED_PHRASES = [
    'Checking what went wrong',
    'Reading the error',
    'Working out what went wrong',
    'Looking into the failure',
];

/** The reply to the operator's message, before any call. */
export const STARTING_PHRASES = [
    'Thinking',
    'Thinking it over',
    'Reading your message',
    'Considering the question',
    'Getting started',
    'Working on it',
];

/** Under a card the model drew. */
export const AFTER_WIDGET_PHRASES = CARRYING_ON.any;

/** Under a change awaiting the operator's approval. */
export const WRITE_PENDING_PHRASES = [
    'Waiting for your approval',
    'Waiting for your go-ahead',
    'Holding until you approve',
];

/** Under a change approved and not yet applied. */
export const WRITE_APPLYING_PHRASES = [
    'Applying the change',
    'Making the change',
    'Saving the change',
];

/** Under a change applied. */
export const WRITE_APPLIED_PHRASES = [
    'Checking the change went through',
    'Confirming the change',
    'Reading back the result',
    'Wrapping up the change',
];

/** Under a change the operator declined, or let expire. */
export const WRITE_DECLINED_PHRASES = [
    'Taking your answer into account',
    'Leaving things as they are',
    'Noting that you declined',
    'Carrying on without the change',
];
